import React, { useRef, useState } from "react"
import Popup from "component/popup/popup"
import IconsColor from "util/iconsColor"
import { agent } from "apiClient/api"
import { useSnackbar } from "context/snackbarContext"
import { downloadCsv } from "util/export/csvExport"
import {
    AGENCY_IMPORT_HEADERS,
    buildAgencyImportTemplateCsv,
    checkAgencyImportFile,
    formatFileSize,
    getImportErrorMessage,
    IAgencyImportResult,
    parseAgencyImportResult,
} from "./agencyImportUtil"
import ImportIssuesDrawer from "./importIssuesDrawer"
import ImportSummarySection, { ImportIssueSection } from "./importSummarySection"
import styles from "./importAgencyPopup.module.scss"

type ImportAgencyPopupProps = {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
}

const TEMPLATE_FILENAME = "agency-import-template.csv"

const COLLAPSED_SECTIONS: Record<ImportIssueSection, boolean> = { duplicate: false, missingOrInvalid: false }

const recordsLabel = (count: number): string => `${count} record${count === 1 ? "" : "s"}`

const ImportAgencyPopup: React.FC<ImportAgencyPopupProps> = ({ isOpen, onClose, onSuccess }) => {

    const { showSuccess, showError } = useSnackbar()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [file, setFile] = useState<File | null>(null)
    const [dataRowCount, setDataRowCount] = useState(0)
    const [fileErrors, setFileErrors] = useState<string[]>([])
    const [serverError, setServerError] = useState<string | null>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [result, setResult] = useState<IAgencyImportResult | null>(null)
    const [expandedSections, setExpandedSections] = useState(COLLAPSED_SECTIONS)
    const [drawerSection, setDrawerSection] = useState<ImportIssueSection | null>(null)

    const resetState = () => {
        setFile(null)
        setDataRowCount(0)
        setFileErrors([])
        setServerError(null)
        setIsDragging(false)
        setResult(null)
        setExpandedSections(COLLAPSED_SECTIONS)
        setDrawerSection(null)
        if (fileInputRef.current) fileInputRef.current.value = ""
    }

    const handleClose = () => {
        if (isUploading) return
        resetState()
        onClose()
    }

    const selectFile = async (selected: File | undefined) => {
        if (!selected) return
        setServerError(null)
        setFile(selected)
        try {
            const text = await selected.text()
            const check = checkAgencyImportFile(selected.name, selected.size, text)
            setFileErrors(check.errors)
            setDataRowCount(check.dataRowCount)
        } catch (err) {
            console.error("Read import file error", err)
            setFileErrors(["Could not read the file. Please try again."])
            setDataRowCount(0)
        }
    }

    const handleRemoveFile = () => {
        setFile(null)
        setDataRowCount(0)
        setFileErrors([])
        setServerError(null)
        if (fileInputRef.current) fileInputRef.current.value = ""
    }

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setIsDragging(false)
        void selectFile(event.dataTransfer.files?.[0])
    }

    const handleDownloadTemplate = () => {
        downloadCsv(buildAgencyImportTemplateCsv(), TEMPLATE_FILENAME)
    }

    const handleImport = async () => {
        if (!file || fileErrors.length > 0) return
        setIsUploading(true)
        setServerError(null)
        try {
            const response = await agent.AgencyImportAPI.post(file)
            if (response?.status === true) {
                const importResult = parseAgencyImportResult(response.data)
                setResult(importResult)
                if (importResult.importedCount > 0) {
                    showSuccess(`${recordsLabel(importResult.importedCount)} imported`)
                    onSuccess()
                } else {
                    showError("No records were imported")
                }
            } else {
                setServerError(getImportErrorMessage(response))
            }
        } catch (err) {
            console.error("Import agency data error", err)
            showError("Failed to import agency data")
        } finally {
            setIsUploading(false)
        }
    }

    const renderSelectStep = () => (
        <div className={styles.body}>
            <div className={styles.requirements}>
                <p className={styles.intro}>
                    Upload a CSV to add agencies, offices and agents in bulk. Existing agency + office + agent
                    combinations are skipped, so only new records are created.
                </p>
                <div className={styles.requirementsHeader}>
                    <span className={styles.requirementsTitle}>Required columns</span>
                    <button type="button" className={styles.templateButton} onClick={handleDownloadTemplate}>
                        Download template
                    </button>
                </div>
                <div className={styles.chips}>
                    {AGENCY_IMPORT_HEADERS.map((header) => <span key={header} className={styles.chip}>{header}</span>)}
                </div>
                <p className={styles.requirementsHint}>
                    One row per agent. Phone as a 10-digit number. A file exported from this page can be re-imported as-is.
                </p>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className={styles.hiddenInput}
                onChange={(event) => void selectFile(event.target.files?.[0])}
            />

            {!file ? (
                <div
                    role="button"
                    tabIndex={0}
                    className={`${styles.dropzone}${isDragging ? ` ${styles.dropzoneActive}` : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") fileInputRef.current?.click()
                    }}
                    onDragOver={(event) => {
                        event.preventDefault()
                        setIsDragging(true)
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                >
                    <IconsColor iconName="uploadIcon" width="32" height="32" fillColor="none" strokeColor="#1565C0" />
                    <p className={styles.dropzoneTitle}>
                        Drag and drop your CSV here, or <span className={styles.linkText}>browse</span>
                    </p>
                    <p className={styles.dropzoneHint}>.csv only, up to 5 MB</p>
                </div>
            ) : (
                <div className={`${styles.fileCard}${fileErrors.length > 0 ? ` ${styles.fileCardError}` : ""}`}>
                    <IconsColor iconName="fileIcon" width="28" height="28" fillColor="none" strokeColor="#1565C0" />
                    <div className={styles.fileMeta}>
                        <span className={styles.fileName} title={file.name}>{file.name}</span>
                        <span className={styles.fileDetails}>
                            {formatFileSize(file.size)}
                            {fileErrors.length === 0 && ` · ${dataRowCount} row${dataRowCount === 1 ? "" : "s"} detected`}
                        </span>
                    </div>
                    <button
                        type="button"
                        className={styles.removeFileButton}
                        onClick={handleRemoveFile}
                        disabled={isUploading}
                        aria-label="Remove file"
                    >
                        <IconsColor iconName="closeIcon" width="16" height="16" fillColor="none" strokeColor="#7a7a7a" />
                    </button>
                </div>
            )}

            {fileErrors.length > 0 && (
                <div className={styles.errorBox}>
                    <strong>This file can't be imported</strong>
                    <ul>
                        {fileErrors.map((message) => <li key={message}>{message}</li>)}
                    </ul>
                </div>
            )}

            {serverError && (
                <div className={styles.errorBox}>
                    <strong>{serverError}</strong>
                </div>
            )}

        </div>
    )

    const toggleSection = (section: ImportIssueSection) =>
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))

    const renderResultStep = (importResult: IAgencyImportResult) => (
        <div className={styles.body}>
            <p className={styles.resultFileName}>File: {file?.name}</p>

            <div className={styles.totalCard}>
                <IconsColor iconName="fileIcon" width="22" height="22" fillColor="none" strokeColor="#0a0a0a" />
                <div>
                    <p className={styles.totalLabel}>Total records found</p>
                    <p className={styles.totalValue}>{importResult.totalRecords}</p>
                </div>
            </div>

            <ImportSummarySection
                section="duplicate"
                count={importResult.duplicateCount}
                rows={importResult.duplicateRows}
                expanded={expandedSections.duplicate}
                onToggle={() => toggleSection("duplicate")}
                onViewAll={() => setDrawerSection("duplicate")}
            />

            <ImportSummarySection
                section="missingOrInvalid"
                count={importResult.missingOrInvalidCount}
                rows={importResult.missingOrInvalidRows}
                expanded={expandedSections.missingOrInvalid}
                onToggle={() => toggleSection("missingOrInvalid")}
                onViewAll={() => setDrawerSection("missingOrInvalid")}
            />

            {importResult.importedCount > 0 ? (
                <div className={`${styles.outcomeCard} ${styles.outcomeSuccess}`}>
                    <IconsColor iconName="checkCircleIcon" width="20" height="20" fillColor="none" strokeColor="#15803D" />
                    <span>{recordsLabel(importResult.importedCount)} imported</span>
                </div>
            ) : (
                <div className={`${styles.outcomeCard} ${styles.outcomeNeutral}`}>
                    <IconsColor iconName="errorIcon" width="20" height="20" fillColor="none" strokeColor="#6b7280" />
                    <span>No records were imported</span>
                </div>
            )}
        </div>
    )

    const drawerRows =
        drawerSection === "duplicate" ? result?.duplicateRows : result?.missingOrInvalidRows
    const drawerCount =
        drawerSection === "duplicate" ? result?.duplicateCount : result?.missingOrInvalidCount

    return (
        <>
            <Popup
                isOpen={isOpen}
                onClose={handleClose}
                title={result ? "Import Summary" : "Import Agencies"}
                size="medium"
                closeOnOverlayClick={!isUploading}
                secondaryButton={
                    result
                        ? { label: "Import Another File", onClick: resetState }
                        : { label: "Cancel", onClick: handleClose, disabled: isUploading }
                }
                primaryButton={
                    result
                        ? { label: "Done", onClick: handleClose }
                        : {
                            label: isUploading ? "Importing..." : "Import",
                            onClick: handleImport,
                            disabled: !file || fileErrors.length > 0 || isUploading,
                        }
                }
            >
                {result ? renderResultStep(result) : renderSelectStep()}
            </Popup>

            {isOpen && drawerSection && drawerRows && (
                <ImportIssuesDrawer
                    section={drawerSection}
                    count={drawerCount ?? drawerRows.length}
                    rows={drawerRows}
                    onClose={() => setDrawerSection(null)}
                />
            )}
        </>
    )
}

export default ImportAgencyPopup
